import { createSelector } from '@ngrx/store';
import { ClearData, TransactionTypesEnum } from '@shared/models';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { ILetterUploadState } from '@store/reducers/letter-upload/letter-upload.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectClearData } from '@store/selectors/document-upload/document-upload.selectors';
import { selectInternalTransferTransactionType } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectLetterUploadState } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectTransactionType } from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import {
  selectClientPersonalInfoData,
  selectEmploymentFeature,
  selectFinancialsState,
  selectInvestmentObjectivesState,
  selectIsPersonalInfoSSN,
  selectPersonalAddressFeature,
  selectPersonalPhone,
  VgEmploymentState,
  VgPersonalAddressState,
} from 'vg-interaction-model';
import { IFinancialsState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/financials.reducer';
import { IInvestmentObjectivesState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/investment-objectives.reducer';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';
import { IPersonalPhoneState } from 'vg-interaction-model/dist/lib/features/personal-phone/store/reducers/personal-phone.reducer';

import {
  buildEmploymentReceipt,
  buildFinancialProfileReceipt,
  buildProfileReceipt,
} from './new-account.cashier';

export const selectProfileReceipt = (
  editable: boolean,
  content?: typeof ReviewContent.receiptContent.profile,
) =>
  createSelector(
    selectTriageState,
    selectTransactionType,
    selectClientPersonalInfoData,
    selectIsPersonalInfoSSN,
    selectPersonalAddressFeature,
    selectPersonalPhone,
    selectClearData,
    (
      guideState: TriageState,
      transactionType: TransactionTypesEnum,
      personalInfo: IPersonalInfoClientState['clientData'],
      ssn_private: string,
      address: VgPersonalAddressState,
      phone: IPersonalPhoneState,
      clearData: ClearData,
    ) => {
      return buildProfileReceipt(
        guideState,
        transactionType,
        personalInfo,
        ssn_private,
        address,
        phone,
        clearData,
        editable,
        content,
      );
    },
  );

export const selectEmploymentReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.employment,
) =>
  createSelector(
    selectEmploymentFeature,
    selectTransactionType,
    selectInternalTransferTransactionType,
    selectTriageState,
    selectLetterUploadState,
    (
      state: VgEmploymentState,
      transactionType: TransactionTypesEnum,
      internalTransferTransactionType: TransactionTypesEnum,
      triageState: TriageState,
      letterUploadState: ILetterUploadState,
    ) =>
      buildEmploymentReceipt(
        state,
        transactionType,
        internalTransferTransactionType,
        triageState,
        letterUploadState,
        editable,
        content,
      ),
  );

export const selectFinancialProfileReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.financialProfile,
) =>
  createSelector(
    selectFinancialsState,
    selectInvestmentObjectivesState,
    selectTransactionType,
    selectInternalTransferTransactionType,
    selectTriageState,
    (
      financial: IFinancialsState,
      invObj: IInvestmentObjectivesState,
      transactionType: TransactionTypesEnum,
      internalTransferTransactionType: TransactionTypesEnum,
      triageState: TriageState,
    ) => {
      return buildFinancialProfileReceipt(
        financial,
        invObj,
        editable,
        transactionType,
        internalTransferTransactionType,
        triageState,
        content,
      );
    },
  );
